// src/components/ui/ParticleText.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';

interface ParticleTextProps {
  text: string;
  isLoading?: boolean;
  className?: string;
  style?: React.CSSProperties;
  fontSize?: number;
  fontWeight?: number;
}

interface Particle {
  x: number;
  y: number;
  originX: number;
  originY: number;
  targetX: number;
  targetY: number;
  vx: number;
  vy: number;
  opacity: number;
  size: number;
  color: string;
  delay: number; // Staggered animation delay
  life: number; // For sparkle effect
}

type AnimationPhase = 'idle' | 'disintegrating' | 'coalescing' | 'complete';

// Magical teal/cyan/blue/purple palette from AggregateScoreOrbit
const MAGIC_COLORS = [
  '#1dd1a1', // Teal
  '#10ac84', // Deep teal
  '#00d2d3', // Cyan
  '#48dbfb', // Light blue
  '#0abde3', // Sky blue
  '#54a0ff', // Blue
  '#2e86de', // Deep blue
  '#5f27cd', // Purple
  '#341f97', // Deep purple
];

export const ParticleText: React.FC<ParticleTextProps> = ({
  text,
  isLoading = false,
  className,
  style,
  fontSize = 22,
  fontWeight = 600,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const phaseRef = useRef<AnimationPhase>('idle');
  const progressRef = useRef(0);

  const [displayText, setDisplayText] = useState(text);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const prevTextRef = useRef(text);
  const pendingTextRef = useRef<string | null>(null);

  // Get random color from magic palette
  const getRandomColor = () => MAGIC_COLORS[Math.floor(Math.random() * MAGIC_COLORS.length)];

  // Extract particles from text rendered on canvas
  const extractParticles = useCallback((
    ctx: CanvasRenderingContext2D,
    textToRender: string,
    width: number,
    height: number
  ): Particle[] => {
    const particles: Particle[] = [];

    // Clear and render text in white for pixel detection
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#ffffff';
    ctx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    ctx.textBaseline = 'top';

    // Word wrap for multi-line text
    const words = textToRender.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    const maxWidth = width - 10;

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    if (currentLine) lines.push(currentLine);

    // Render lines
    const lineHeight = fontSize * 1.4;
    lines.forEach((line, i) => {
      ctx.fillText(line, 5, i * lineHeight + 5);
    });

    // Extract pixel data
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    // Sample pixels to create particles (every 2-3 pixels for denser effect)
    const gap = 2;
    let particleIndex = 0;

    for (let y = 0; y < height; y += gap) {
      for (let x = 0; x < width; x += gap) {
        const index = (y * width + x) * 4;
        const alpha = data[index + 3];

        if (alpha > 50) {
          // Calculate staggered delay based on position (wave effect)
          const distFromCenter = Math.sqrt(
            Math.pow(x - width / 2, 2) + Math.pow(y - height / 2, 2)
          );
          const maxDist = Math.sqrt(Math.pow(width / 2, 2) + Math.pow(height / 2, 2));
          const normalizedDist = distFromCenter / maxDist;

          particles.push({
            x,
            y,
            originX: x,
            originY: y,
            targetX: x,
            targetY: y,
            vx: 0,
            vy: 0,
            opacity: 0,
            size: 1 + Math.random() * 1.5, // Varied sizes for sparkle
            color: getRandomColor(),
            delay: normalizedDist * 0.3 + Math.random() * 0.1, // Staggered delay
            life: Math.random(), // For twinkling
          });
          particleIndex++;
        }
      }
    }

    return particles;
  }, [fontSize, fontWeight]);

  // Calculate canvas size based on text
  const measureText = useCallback((textToMeasure: string): { width: number; height: number } => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return { width: 300, height: 100 };

    ctx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;

    const containerWidth = containerRef.current?.offsetWidth || 600;
    const maxWidth = containerWidth - 10;
    const words = textToMeasure.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });
    if (currentLine) lines.push(currentLine);

    const lineHeight = fontSize * 1.4;
    const width = Math.min(containerWidth, maxWidth + 20);
    const height = Math.max(lines.length * lineHeight + 20, fontSize + 20);

    return { width, height };
  }, [fontSize, fontWeight]);

  // Animation loop
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const particles = particlesRef.current;
    const phase = phaseRef.current;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (phase === 'disintegrating') {
      // Very slow, magical disintegration
      progressRef.current += 0.006;
      const progress = progressRef.current;

      particles.forEach((p, i) => {
        // Staggered start based on delay
        const adjustedProgress = Math.max(0, progress - p.delay);

        if (adjustedProgress > 0) {
          // Initialize velocity on first frame of this particle's animation
          if (adjustedProgress < 0.02 && p.vx === 0 && p.vy === 0) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 0.8 + Math.random() * 1.5; // Very slow, dreamy scatter
            p.vx = Math.cos(angle) * speed;
            p.vy = Math.sin(angle) * speed - 0.5; // Gentle upward float
          }

          // Update position with velocity and drag
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= 0.985; // Heavy drag for floatier feel
          p.vy *= 0.985;
          p.vy += 0.008; // Very light gravity - particles float

          // Fade out with sparkle
          const fadeProgress = Math.min(adjustedProgress * 1.5, 1);
          const sparkle = 0.8 + Math.sin(p.life * 20 + progress * 10) * 0.2;
          p.opacity = Math.max(0, (1 - fadeProgress) * sparkle);
          p.life += 0.02;

          // Draw particle with glow effect
          if (p.opacity > 0.01) {
            // Outer glow
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 2, 0, Math.PI * 2);
            ctx.fillStyle = p.color;
            ctx.globalAlpha = p.opacity * 0.3;
            ctx.fill();

            // Core particle
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
            ctx.fillStyle = p.color;
            ctx.globalAlpha = p.opacity;
            ctx.fill();
          }
        } else {
          // Particle hasn't started animating yet - draw in place
          const sparkle = 0.7 + Math.sin(p.life * 15) * 0.3;
          p.life += 0.01;

          ctx.beginPath();
          ctx.arc(p.originX, p.originY, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = sparkle;
          ctx.fill();
        }
      });

      ctx.globalAlpha = 1;

      if (progress >= 1.5) { // Longer duration
        // Switch to coalescing phase
        phaseRef.current = 'coalescing';
        progressRef.current = 0;

        // Get new text particles
        const newText = pendingTextRef.current || text;
        setDisplayText(newText);

        // Recalculate size for new text
        const newSize = measureText(newText);
        setCanvasSize(newSize);

        // Wait a frame for canvas to resize, then extract new particles
        requestAnimationFrame(() => {
          const newCanvas = canvasRef.current;
          const newCtx = newCanvas?.getContext('2d');
          if (newCanvas && newCtx) {
            const newParticles = extractParticles(newCtx, newText, newSize.width, newSize.height);

            // Position particles in a magical dispersed cloud
            newParticles.forEach(p => {
              const angle = Math.random() * Math.PI * 2;
              const distance = 80 + Math.random() * 120; // Wider dispersion
              p.x = p.targetX + Math.cos(angle) * distance;
              p.y = p.targetY + Math.sin(angle) * distance - 30; // Start higher
              p.opacity = 0;
              p.vx = 0;
              p.vy = 0;
              p.life = Math.random();
            });

            particlesRef.current = newParticles;
          }
        });
      }
    } else if (phase === 'coalescing') {
      // Very slow, magical coalescing
      progressRef.current += 0.008;
      const progress = Math.min(progressRef.current, 1);

      // Smooth easing for magical feel
      const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);
      const easeInOutSine = (t: number) => -(Math.cos(Math.PI * t) - 1) / 2;
      const easedProgress = easeOutQuart(progress);

      particles.forEach((p) => {
        // Staggered coalescing based on delay
        const adjustedProgress = Math.max(0, progress - p.delay * 0.5);
        const particleEase = easeOutQuart(Math.min(adjustedProgress * 1.5, 1));

        // Very smooth, slow interpolation to target
        const lerpFactor = 0.03 + particleEase * 0.06;
        p.x = p.x + (p.targetX - p.x) * lerpFactor;
        p.y = p.y + (p.targetY - p.y) * lerpFactor;

        // Fade in with sparkle
        const sparkle = 0.85 + Math.sin(p.life * 15 + progress * 8) * 0.15;
        p.opacity = Math.min(1, adjustedProgress * 2) * sparkle;
        p.life += 0.015;

        // Draw with glow
        if (p.opacity > 0.01) {
          // Soft outer glow
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = p.opacity * 0.2;
          ctx.fill();

          // Core particle
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = p.opacity;
          ctx.fill();
        }
      });

      ctx.globalAlpha = 1;

      if (progress >= 1) {
        phaseRef.current = 'complete';
        progressRef.current = 0;
        setIsAnimating(false);
        pendingTextRef.current = null;

        // Clear canvas - text will show through the regular DOM
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        return;
      }
    }

    if (phase !== 'complete' && phase !== 'idle') {
      animationRef.current = requestAnimationFrame(animate);
    }
  }, [text, measureText, extractParticles]);

  // Start disintegration animation
  const startAnimation = useCallback((newText: string) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    pendingTextRef.current = newText;
    setIsAnimating(true);

    // Extract particles from current text
    const particles = extractParticles(ctx, displayText, canvas.width, canvas.height);
    particlesRef.current = particles;

    // Start disintegration
    phaseRef.current = 'disintegrating';
    progressRef.current = 0;

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    animationRef.current = requestAnimationFrame(animate);
  }, [displayText, extractParticles, animate]);

  // Handle text changes
  useEffect(() => {
    if (text !== prevTextRef.current && !isLoading) {
      startAnimation(text);
      prevTextRef.current = text;
    }
  }, [text, isLoading, startAnimation]);

  // Handle loading state changes
  useEffect(() => {
    if (!isLoading && pendingTextRef.current === null && text !== displayText) {
      startAnimation(text);
    }
  }, [isLoading, text, displayText, startAnimation]);

  // Initial setup
  useEffect(() => {
    const size = measureText(displayText);
    setCanvasSize(size);
  }, []);

  // Update canvas size when container resizes
  useEffect(() => {
    const handleResize = () => {
      if (!isAnimating) {
        const size = measureText(displayText);
        setCanvasSize(size);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [displayText, isAnimating, measureText]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Initial text display without animation
  useEffect(() => {
    if (!isAnimating && displayText === '' && text) {
      setDisplayText(text);
      const size = measureText(text);
      setCanvasSize(size);
    }
  }, [text, displayText, isAnimating, measureText]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        ...style,
        position: 'relative',
        minHeight: canvasSize.height || fontSize * 1.5,
        width: '100%',
      }}
    >
      {/* Canvas for particle animation */}
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          opacity: isAnimating ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Actual text (hidden during animation) */}
      <span
        style={{
          display: 'inline-block',
          opacity: isAnimating ? 0 : 1,
          transition: 'opacity 0.4s ease',
          fontSize: `${fontSize}px`,
          fontWeight,
          lineHeight: 1.4,
        }}
      >
        {displayText || text}
      </span>
    </div>
  );
};

export default ParticleText;
